import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Permission } from '../../models/identity/permission.enum';
import AccessDenied from '../access-denied/AccessDenied';
import Loading from '../loading/Loading';

type AccessRule = {
  permissions: Permission[];
  dealerOnly?: boolean;
};

const readWrite = (...permissions: Permission[]): AccessRule => ({ permissions });

const getAccessRule = (pathname: string): AccessRule | null => {
  const relativePath = pathname.replace(/^\/management\/?/, '').replace(/\/$/, '');
  const [section, secondSegment, thirdSegment] = relativePath.split('/');

  switch (section) {
    case 'admin':
      return null;

    case 'companies':
      return secondSegment
        ? readWrite(Permission.ReadWriteCompany, Permission.FullControlCompany)
        : readWrite(
            Permission.ReadCompany,
            Permission.ReadWriteCompany,
            Permission.FullControlCompany
          );

    case 'organizations':
      if (secondSegment === 'new') {
        return readWrite(
          Permission.ReadWriteOrganization,
          Permission.FullControlOrganization,
          Permission.ReadWriteCompany,
          Permission.FullControlCompany
        );
      }
      return readWrite(
        Permission.ReadOrganization,
        Permission.ReadWriteOrganization,
        Permission.FullControlOrganization,
        Permission.ReadCompany,
        Permission.ReadWriteCompany,
        Permission.FullControlCompany
      );

    case 'stores': {
      if (secondSegment === 'dealer') {
        return {
          ...readWrite(Permission.ReadWriteWarehouse, Permission.FullControlWarehouse),
          dealerOnly: true,
        };
      }

      if (secondSegment && thirdSegment === 'view') {
        return {
          ...readWrite(
            Permission.ReadWarehouse,
            Permission.ReadWriteWarehouse,
            Permission.FullControlWarehouse
          ),
          dealerOnly: true,
        };
      }

      return secondSegment
        ? readWrite(Permission.ReadWriteWarehouse, Permission.FullControlWarehouse)
        : readWrite(
            Permission.ReadWarehouse,
            Permission.ReadWriteWarehouse,
            Permission.FullControlWarehouse
          );
    }

    case 'point-sales':
      return secondSegment
        ? readWrite(Permission.ReadWritePointsOfSale, Permission.FullControlPointsOfSale)
        : readWrite(
            Permission.ReadPointsOfSale,
            Permission.ReadWritePointsOfSale,
            Permission.FullControlPointsOfSale
          );

    case 'branches':
      return secondSegment
        ? readWrite(Permission.ReadWriteBranch, Permission.FullControlBranch)
        : readWrite(
            Permission.ReadBranch,
            Permission.ReadWriteBranch,
            Permission.FullControlBranch
          );

    case 'coupons':
      return secondSegment
        ? readWrite(Permission.ReadWriteCoupon, Permission.FullControlCoupon)
        : readWrite(
            Permission.ReadCoupon,
            Permission.ReadWriteCoupon,
            Permission.FullControlCoupon
          );

    case 'catalogs':
      return {
        ...(secondSegment === 'new'
          ? readWrite(Permission.ReadWriteCatalog, Permission.FullControlCatalog)
          : readWrite(
              Permission.ReadCatalog,
              Permission.ReadWriteCatalog,
              Permission.FullControlCatalog
            )),
        dealerOnly: true,
      };

    case 'discounts':
      return {
        ...(secondSegment
          ? readWrite(Permission.ReadWriteDiscount, Permission.FullControlDiscount)
          : readWrite(
              Permission.ReadDiscount,
              Permission.ReadWriteDiscount,
              Permission.FullControlDiscount
            )),
        dealerOnly: true,
      };

    case 'freight':
      return {
        ...(secondSegment
          ? readWrite(Permission.ReadWriteFreight, Permission.FullControlFreight)
          : readWrite(
              Permission.ReadFreight,
              Permission.ReadWriteFreight,
              Permission.FullControlFreight
            )),
        dealerOnly: true,
      };

    case 'promotions':
      return secondSegment
        ? readWrite(Permission.ReadWritePromotion, Permission.FullControlPromotion)
        : readWrite(
            Permission.ReadPromotion,
            Permission.ReadWritePromotion,
            Permission.FullControlPromotion
          );

    case 'zones':
      return {
        ...(secondSegment
          ? readWrite(Permission.ReadWriteZone, Permission.FullControlZone)
          : readWrite(Permission.ReadZone, Permission.ReadWriteZone, Permission.FullControlZone)),
        dealerOnly: true,
      };

    case 'users':
      if (secondSegment === 'roles') {
        return thirdSegment
          ? readWrite(Permission.ReadWriteRole, Permission.FullControlRole)
          : readWrite(Permission.ReadRole, Permission.ReadWriteRole, Permission.FullControlRole);
      }
      if (secondSegment) {
        return readWrite(Permission.ReadWriteUser, Permission.FullControlUser);
      }
      return readWrite(
        Permission.ReadUser,
        Permission.ReadWriteUser,
        Permission.FullControlUser,
        Permission.ReadRole,
        Permission.ReadWriteRole,
        Permission.FullControlRole
      );

    case 'prices':
      return secondSegment
        ? readWrite(Permission.ReadWriteSuggestedPrice, Permission.FullControlSuggestedPrice)
        : readWrite(
            Permission.ReadSuggestedPrice,
            Permission.ReadWriteSuggestedPrice,
            Permission.FullControlSuggestedPrice
          );

    case 'cash-closing':
      return {
        ...readWrite(Permission.ReadCashClosing, Permission.ReadWriteCashClosing),
        dealerOnly: true,
      };

    case 'reports':
      if (secondSegment === 'kardex') {
        return {
          ...readWrite(Permission.ControlKardexReport),
          dealerOnly: true,
        };
      }
      return null;

    default:
      return null;
  }
};

const ManagementPermissionGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, selectedPos, permissionMatch } = useAuth();

  const rule = getAccessRule(location.pathname);

  if (!rule) return <Outlet />;

  if (!currentUser || (rule.dealerOnly && !selectedPos)) {
    return <Loading open message="Validando permisos..." />;
  }

  const hasPermission = Boolean(permissionMatch(...rule.permissions));
  const dealerAccess = !rule.dealerOnly || selectedPos?.isDealer === true;

  if (!hasPermission || !dealerAccess) {
    return (
      <AccessDenied
        message="No tienes permisos para acceder a esta sección."
        actionText="Volver a Inicio"
        onActionClick={() => navigate('/statistics', { replace: true })}
      />
    );
  }

  return <Outlet />;
};

export default ManagementPermissionGuard;
